import { useEffect, useState } from 'react'
import type {
  CharacterSheet,
  CharacterSummary,
  HpAction,
  HpProblem,
  InventoryAction,
  InventoryProblem,
  RollEntry,
  RollReason,
  SpellAction,
  SpellProblem,
  ResourceAction,
  ResourceProblem,
  EditRequestState,
} from '@rollcall/contracts'
import type { RollMode } from '@rollcall/rules'
import {
  ApiError,
  CreationRefused,
  HpRefused,
  InventoryRefused,
  SpellRefused,
  ResourceRefused,
  type Api,
} from '../api.js'
import { failureText } from '../calc-error.js'
import type { TabKey } from '../tabs.js'
import { characterSubtitle } from '../heading.js'
import { ExperienceBar, InitiativePanel, RollFeed, useTableState } from './master.js'
import { contentName } from './labels.js'
import type { Heading } from './app.js'
import { CombatTab } from './combat.js'
import { RollPanel } from './roll-panel.js'
import { LevelUp } from './level-up.js'
import { useT } from './i18n-context.js'
import { ExportMenu, ImportPanel } from './transfer.js'
import { InventoryTab } from './inventory.js'
import { CharacterSheetTab } from './sheet.js'
import { SpellsTab } from './spells.js'
import { useWide } from './viewport.js'

/**
 * Список персонажей и чтение листа.
 *
 * Лист не хранится: сервер собирает его свёрткой журнала (D4) и отдаёт
 * вместе с провенансом (D5). Клиент показывает то, что пришло, и ничего
 * не пересчитывает (D3) — поэтому здесь нет ни одной арифметики.
 *
 * Содержимое вкладок — следующий заход; пока лист показан сводкой:
 * величина и её значение.
 *
 * Драфты и персонажи кампании стоят одним списком (D29): для того,
 * кто их открывает, это одинаковые персонажи. Различает их подпись —
 * у драфта её и хватает, потому что «без кампании» это всё, что
 * о нём можно сказать до того, как мастер его подберёт.
 */

export function CharacterList({
  api,
  campaignId,
  onCreate,
  onOpen,
}: {
  readonly api: Api
  readonly campaignId: string | null
  readonly onCreate: () => void
  readonly onOpen: (character: CharacterSummary) => void
}) {
  const t = useT()
  const [characters, setCharacters] = useState<readonly CharacterSummary[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    // Кампании может не быть — тогда список состоит из одних драфтов
    // этого устройства (D29). Пустой список раньше был единственным
    // ответом на «нет кампании», и созданный драфт было не найти.
    let cancelled = false
    void api
      .listCharacters(campaignId)
      .then((list) => {
        if (!cancelled) setCharacters(list)
      })
      .catch(() => {
        if (!cancelled) {
          setCharacters([])
          setFailure(t('errors.network'))
        }
      })
    return () => {
      cancelled = true
    }
  }, [api, campaignId, t])

  return (
    <section className="app-screen" aria-labelledby="characters-title">
      <h2 id="characters-title">{t('characters.title')}</h2>

      {failure !== null && (
        <p className="app-problems" role="alert">
          {failure}
        </p>
      )}

      {characters === null ? (
        <p>{t('characters.loading')}</p>
      ) : characters.length === 0 ? (
        <p>{t('characters.empty')}</p>
      ) : (
        <ul className="app-list">
          {characters.map((character) => (
            <li key={character.id}>
              <button
                type="button"
                className="app-value-button"
                data-draft={character.campaignId === null ? '' : undefined}
                onClick={() => {
                  onOpen(character)
                }}
              >
                <span className="app-value-label">{character.name}</span>
                <span className="app-value-note">
                  {character.campaignId === null
                    ? t('characters.draft')
                    : t('characters.inCampaign')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="app-actions">
        <button type="button" className="app-button" onClick={onCreate}>
          {t('characters.create')}
        </button>
      </div>

      {/* Загрузка файла стоит рядом с созданием: это второй способ
          завести персонажа, а не настройка (D54). */}
      <ImportPanel api={api} onImported={onOpen} />

      {/* Персонаж без кампании — не половина персонажа: ему не хватает
          только стола, и попадает он туда по ссылке на место, а не
          тем, что лежит в аккаунте (D29, D33). */}
      {(characters ?? []).some((character) => character.campaignId === null) && (
        <p className="app-field-hint">{t('characters.draftHint')}</p>
      )}
    </section>
  )
}

/**
 * Лист по идентификатору из адреса.
 *
 * Именно по идентификатору, а не по объекту персонажа: прямая ссылка
 * на лист обязана открываться с нуля, без прохождения по списку
 * (D27). Всё, что нужно показать, приходит вместе с листом.
 */
export function CharacterSheetView({
  api,
  characterId,
  tab,
  signedIn = false,
  buildTo = null,
  onHeading,
  onEditChoices,
  onPrint,
  onBack,
}: {
  readonly api: Api
  readonly characterId: string
  /**
   * Открыть визард на правку выборов (D62); не задано — правки нет
   * (лист открыт не тем, кто им владеет).
   */
  readonly onEditChoices?: (() => void) | undefined
  /** Открыть печатный лист (D64); не задано — пункта в меню нет. */
  readonly onPrint?: (() => void) | undefined
  /**
   * Докуда идёт сборка персонажа (D60); `null` — никуда, обычный лист.
   *
   * Кампания стартует с третьего уровня, а визард создаёт первый:
   * дальше игрок проходит повышения подряд, не выходя на лист. Цель
   * живёт здесь, а не у персонажа: это намерение игрока на ближайшие
   * пять минут, а не его свойство. Ушёл со страницы — персонаж
   * остался на достигнутом уровне, и добрать можно обычным способом.
   */
  readonly buildTo?: number | null | undefined
  /** Какую вкладку листа показать: лист читается один на все. */
  readonly tab: TabKey
  /**
   * Вошёл аккаунтом — значит, это мастер (D8): игрок пользователем
   * не является. От этого зависит одно: предлагать ли скрытый бросок
   * (D12). Право всё равно проверяет сервер.
   */
  readonly signedIn?: boolean | undefined
  /** Кого показывает шапка: имя и «вид, класс, подкласс, уровень». */
  readonly onHeading?: ((heading: Heading | null) => void) | undefined
  readonly onBack: () => void
}) {
  const t = useT()
  /**
   * Широкий экран (D52): содержимое всех четырёх вкладок стоит рядом,
   * и выбранная вкладка перестаёт что-либо решать. На узком показана
   * одна — та, что выбрана нижними вкладками (D10).
   */
  const wide = useWide()
  const shows = (which: TabKey) => wide || tab === which
  const [sheet, setSheet] = useState<CharacterSheet | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  // Предмет, по которому ждём ответ, и отказ по нему. Оба живут
  // здесь, а не во вкладке: лист приходит ответом на команду,
  // и держать их врозь значило бы обновлять экран в два приёма.
  const [pending, setPending] = useState<string | null>(null)
  const [problems, setProblems] = useState<readonly InventoryProblem[]>([])
  const [hpProblems, setHpProblems] = useState<readonly HpProblem[]>([])
  const [spellProblems, setSpellProblems] = useState<readonly SpellProblem[]>([])
  const [resourceProblems, setResourceProblems] = useState<readonly ResourceProblem[]>(
    [],
  )
  const [commandFailure, setCommandFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [rolls, setRolls] = useState<readonly RollEntry[]>([])
  // Повышение уровня — отдельный экран поверх вкладки: на нём свои
  // выборы, и мешать их с листом значит показывать два разных
  // персонажа сразу (D31).
  const [levelling, setLevelling] = useState(false)
  /**
   * Сколько уровней осталось до цели сборки (D60).
   *
   * Считается от листа, а не хранится: лист приходит с сервера после
   * каждого повышения, и второй счётчик разошёлся бы с ним на первом
   * же откате уровня.
   */
  const left = sheet === null || buildTo === null ? 0 : buildTo - sheet.level
  /**
   * Судьба последней правки (D62): ждёт мастера или отклонена —
   * и почему. Отказ без причины оставляет гадать, что чинить (D61).
   */
  const [editRequest, setEditRequest] = useState<EditRequestState | null>(null)
  useEffect(() => {
    let cancelled = false
    // Через `Promise.resolve`: строка о правке — подсказка, и её отказ,
    // в том числе синхронный, не должен ронять лист.
    void Promise.resolve()
      .then(() => api.creationEdit(characterId))
      .then((edit) => {
        if (!cancelled) setEditRequest(edit.request)
      })
      .catch(() => {
        // Нет правки — нет и строки о ней: лист от этого не страдает.
      })
    return () => {
      cancelled = true
    }
  }, [api, characterId])
  const [levelProblems, setLevelProblems] = useState<
    Readonly<Record<string, readonly string[]>>
  >({})
  // Что сделала последняя команда: «уровень поднят», «уровень откачен».
  // Без этого повышение выглядит как ничего: экран закрывается,
  // и понять, случилось ли что-нибудь, можно только по числам.
  const [notice, setNotice] = useState<string | null>(null)
  // Роллер живёт панелью поверх вкладки: результат последнего броска
  // виден сразу, без прокрутки к журналу (D12).
  const [rolling, setRolling] = useState(false)
  /**
   * Последний бросок целиком, а не только его причина.
   *
   * Причины мало: у своего броска запись («2d6+3») живёт рядом с ней,
   * и повтор без записи сервер отклоняет — «проверьте запись»
   * на броске, который только что прошёл. Панель при этом остаётся
   * с испорченным состоянием: закрытие не помогает, перезагрузка
   * помогает, потому что состояние живёт здесь, а не в панели.
   */
  const [lastRoll, setLastRoll] = useState<{
    reason: RollReason
    notation: string | null
  } | null>(null)
  // Преимущество и помеха — обстоятельство броска, а не свойство того,
  // по чему бросают: переключатель один на лист, и панель с вкладкой
  // делят его, а не заводят каждая свой.
  const [mode, setMode] = useState<RollMode>('normal')
  // Сколько опыта начислить себе: только у драфта, у стола это делает
  // мастер (D31).
  const [selfXp, setSelfXp] = useState(50)
  // Скрытый бросок: обстоятельство броска, как преимущество,
  // и живёт он рядом с ним — одним признаком на лист.
  const [hiddenRoll, setHiddenRoll] = useState(false)

  /**
   * На широком экране роллер стоит сбоку постоянно (D52), а не
   * выезжает поверх листа: место под него есть, и накрывать
   * содержимое ему незачем. Закрыть его по-прежнему можно — тогда
   * он останется закрытым: условие срабатывает на смену ширины,
   * а не на каждую отрисовку.
   */
  useEffect(() => {
    if (wide) setRolling(true)
  }, [wide])

  /**
   * Сборка до заданного уровня (D60): пока не дошли, открыт экран
   * повышения, а не лист.
   *
   * Условие на `left`, а не «один раз после создания»: лист приходит
   * заново после каждого повышения, и пересчёт от него не даёт
   * промахнуться ни в одну сторону — дошли до цели, экран закрылся.
   */
  useEffect(() => {
    if (left > 0) setLevelling(true)
  }, [left])

  useEffect(() => {
    let cancelled = false
    setSheet(null)
    setFailure(null)
    void api
      .characterSheet(characterId)
      .then((next) => {
        if (!cancelled) setSheet(next)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // Три события, и путать их нельзя. «Нет такого» — чужая или
        // устаревшая ссылка (драфт виден только своему устройству,
        // D29). «Не посчитался» — сеть в порядке, а данные не сходятся,
        // и человеку нужна причина. «Недоступен» — ждать.
        const missing = error instanceof ApiError && error.status === 404
        setFailure(
          missing
            ? t('characters.missing')
            : failureText(t, error, t('errors.network')),
        )
      })
    return () => {
      cancelled = true
    }
  }, [api, characterId, t])

  // Шапка называет персонажа: имя и «вид, класс, подкласс, уровень».
  // Уходя с листа, шапка возвращается к названию приложения — иначе
  // в списке персонажей висело бы имя того, кого уже закрыли.
  useEffect(() => {
    if (onHeading === undefined) return
    onHeading(
      sheet === null
        ? null
        : { title: sheet.character.name, subtitle: characterSubtitle(t, sheet) },
    )
    return () => {
      onHeading(null)
    }
  }, [onHeading, sheet, t])

  /**
   * Поток событий кампании на стороне игрока (D2).
   *
   * Мастер снял хиты — экран игрока должен измениться сам, без
   * «обновите страницу». Что именно поменялось, экран не разбирает:
   * он перечитывает лист. Разбор пришлось бы держать в согласии
   * с сервером, а перечитывание — нет (D3).
   *
   * У драфта кампании нет, и потока у него тоже нет: слушать нечего.
   */
  const campaignId = sheet?.character.campaignId ?? null

  /**
   * Очередь боя и общий журнал бросков.
   *
   * Игрок видит их наравне с мастером: порядок ходов и броски
   * за столом — общее знание (D10, D12). Сводка партии сюда
   * не входит — её сервер отдаёт мастеру и только ему.
   */
  const table = useTableState(api, campaignId)
  const reloadTable = table.reload

  useEffect(() => {
    if (campaignId === null) return

    const refresh = (): void => {
      void api
        .characterSheet(characterId)
        .then(setSheet)
        .catch(() => {
          // Лист не перечитался — на экране остаётся прежний.
          // Показывать ошибку по событию нечего: человек ничего
          // не нажимал.
        })
    }

    const seen = (): void => {
      refresh()
      reloadTable()
    }

    const stream = api.watchCampaign(campaignId, 0, {
      onEvent: seen,
      onResync: seen,
      onState: () => {},
    })
    const wake = (): void => {
      if (document.visibilityState === 'visible') stream.wake()
    }
    document.addEventListener('visibilitychange', wake)
    return () => {
      document.removeEventListener('visibilitychange', wake)
      stream.stop()
    }
  }, [api, characterId, campaignId, reloadTable])

  // Журнал бросков читается отдельно от листа: бросок листа
  // не меняет, и мешать их в один ответ значило бы возить журнал
  // при каждом изменении хитов. Читается он один раз на лист:
  // панель роллера открывается с любой вкладки.
  useEffect(() => {
    let cancelled = false
    void Promise.resolve()
      .then(async () => api.rollLog(characterId))
      .then((entries) => {
        if (!cancelled) setRolls(entries)
      })
      .catch(() => {
        if (!cancelled) setRolls([])
      })
    return () => {
      cancelled = true
    }
  }, [api, characterId])

  if (failure !== null) {
    return (
      <section className="app-screen">
        <p className="app-problems" role="alert">
          {failure}
        </p>
        <div className="app-actions">
          <button
            type="button"
            className="app-button app-button-quiet"
            onClick={onBack}
          >
            {t('characters.back')}
          </button>
        </div>
      </section>
    )
  }

  if (sheet === null) {
    return (
      <section className="app-screen">
        <p>{t('characters.loading')}</p>
      </section>
    )
  }

  /**
   * Действие с инвентарём уходит командой (D3): клиент не трогает
   * ни список, ни КД — он ставит на место лист, который вернул
   * сервер. Отказ не меняет ничего, кроме сообщения у строки.
   */
  /**
   * Ответ любой команды — пересчитанный лист (D3). Клиент ставит его
   * на место и не правит ничего у себя: иначе после отказа на экране
   * осталось бы состояние, которого нет ни в одном журнале.
   */
  const runCommand = (
    command: Promise<CharacterSheet>,
    onRefusal: (error: unknown) => boolean,
  ) => {
    setBusy(true)
    setProblems([])
    setHpProblems([])
    setSpellProblems([])
    setResourceProblems([])
    setCommandFailure(null)
    setNotice(null)
    void command
      .then((next) => {
        setSheet(next)
      })
      .catch((error: unknown) => {
        if (onRefusal(error)) return
        setCommandFailure(failureText(t, error, t('errors.network')))
      })
      .finally(() => {
        setBusy(false)
        setPending(null)
      })
  }

  const inventoryAction = (action: InventoryAction) => {
    setPending(action.item)
    runCommand(api.inventoryAction(characterId, action), (error) => {
      if (error instanceof InventoryRefused) {
        setProblems(error.problems)
        return true
      }
      return false
    })
  }

  /** Счётчик ресурса (D56): отказ приезжает своим кодом, как у хитов. */
  const resourceAction = (action: ResourceAction) => {
    runCommand(api.resourceAction(characterId, action), (error) => {
      if (error instanceof ResourceRefused) {
        setResourceProblems(error.problems)
        return true
      }
      return false
    })
  }

  const hpAction = (action: HpAction) => {
    runCommand(api.hpAction(characterId, action), (error) => {
      if (error instanceof HpRefused) {
        setHpProblems(error.problems)
        return true
      }
      return false
    })
  }

  const spellAction = (action: SpellAction) => {
    runCommand(api.spellAction(characterId, action), (error) => {
      if (error instanceof SpellRefused) {
        setSpellProblems(error.problems)
        return true
      }
      return false
    })
  }

  /**
   * Бросок — команда серверу (D12): кости бросает он. Результат
   * дописывается в журнал сверху, а не перечитывается запросом:
   * ответ команды и есть новая запись.
   */
  const roll = (
    reason: RollReason,
    options: { notation?: string | null; manual?: number | null; mode?: RollMode },
  ) => {
    // Бросок открывает панель: результат виден сразу, а не после
    // прокрутки к журналу внизу вкладки.
    setRolling(true)
    setLastRoll({ reason, notation: options.notation ?? null })
    setBusy(true)
    setCommandFailure(null)
    void api
      .roll({
        characterId,
        reason,
        notation: options.notation ?? null,
        mode: options.mode ?? mode,
        manual: options.manual ?? null,
        hidden: hiddenRoll,
        times: 1,
      })
      .then((result) => {
        setRolls((previous) => [...result.entries, ...previous])
      })
      .catch(() => {
        setCommandFailure(t('roll.refusal'))
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <>
      {left > 0 && (
        <p className="app-screen app-notice" role="status" data-testid="build-progress">
          {t('level.buildLeft', { target: buildTo ?? 0, count: left })}
        </p>
      )}
      {notice !== null && (
        <p className="app-screen app-notice" role="status" data-testid="notice">
          {notice}
        </p>
      )}
      {sheet !== null && (
        <div className="app-screen app-progress">
          <ExperienceBar xp={sheet.xp} />
          {sheet.conditions.length > 0 && (
            <p className="app-value-note" data-testid="sheet-conditions">
              {sheet.conditions
                .map(
                  (item) =>
                    contentName(t, item.id, item.name) +
                    (item.level === null ? '' : ` ${item.level}`),
                )
                .join(', ')}
            </p>
          )}
          {campaignId === null && (
            <div className="app-actions">
              <input
                type="number"
                aria-label={t('master.xp.award')}
                value={selfXp}
                onChange={(event) => {
                  setSelfXp(Number(event.target.value))
                }}
              />
              <button
                type="button"
                className="app-button app-button-quiet"
                disabled={busy}
                onClick={() => {
                  runCommand(api.awardXp(characterId, selfXp), () => false)
                }}
              >
                {t('master.xp.self')}
              </button>
            </div>
          )}
          {campaignId === null && (
            <p className="app-field-hint">{t('master.xp.selfHint')}</p>
          )}
          {editRequest?.status === 'pending' && (
            <p className="app-value-note" role="status" data-testid="edit-pending">
              {t('edit.pending')}
            </p>
          )}
          {editRequest?.status === 'rejected' && (
            <p className="app-value-note" role="status" data-testid="edit-rejected">
              {editRequest.note === null
                ? t('edit.rejected')
                : t('edit.rejectedWhy', { note: editRequest.note })}
            </p>
          )}
          {onEditChoices !== undefined && (
            <div className="app-actions">
              <button
                type="button"
                className="app-button app-button-quiet"
                data-testid="edit-choices"
                onClick={onEditChoices}
              >
                {campaignId === null ? t('edit.open') : t('edit.openSeated')}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Панели вкладок (D52).

          На узком экране показана одна — та, что выбрана нижними
          вкладками (D10). На широком стоят все четыре, разложенные
          в две колонки: бой и персонаж слева, заклинания и инвентарь
          справа. Вкладки при этом перестают быть единственной
          навигацией и с экрана уходят — навигация становится
          прокруткой.

          Обёртки на узком экране — `display: contents`: в потоке
          их нет вовсе, и раскладка телефона остаётся ровно той же,
          какой была. */}
      <div className="app-sheet">
        <div className="app-sheet-pane" data-pane="inventory">
          {shows('inventory') && (
            <InventoryTab
              sheet={sheet}
              onAction={inventoryAction}
              pending={pending}
              problems={problems}
              failure={commandFailure}
            />
          )}
        </div>
        <div className="app-sheet-pane" data-pane="combat">
          {shows('combat') && (
            <CombatTab
              sheet={sheet}
              onHp={hpAction}
              onRest={(kind) => {
                runCommand(
                  api.rest(characterId, kind).then((next) => {
                    setNotice(
                      t(kind === 'long' ? 'combat.restedLong' : 'combat.restedShort', {
                        left: next.combat.hp.hitDice - next.combat.hp.hitDiceSpent,
                      }),
                    )
                    return next
                  }),
                  () => false,
                )
              }}
              onRoll={roll}
              mode={mode}
              onMode={setMode}
              busy={busy}
              problems={hpProblems}
              failure={commandFailure}
            />
          )}
        </div>
        <div className="app-sheet-pane" data-pane="spells">
          {shows('spells') && (
            <SpellsTab
              api={api}
              sheet={sheet}
              onAction={spellAction}
              busy={busy}
              problems={spellProblems}
              failure={commandFailure}
            />
          )}
        </div>
        <div className="app-sheet-pane" data-pane="character">
          {shows('character') &&
            (levelling ? (
              <LevelUp
                // Новый уровень — новый экран: план и выборы его, а не
                // прошлого (D60).
                key={sheet.level}
                api={api}
                sheet={sheet}
                busy={busy}
                problems={levelProblems}
                onLevelUp={(input) => {
                  setLevelProblems({})
                  runCommand(
                    api.levelUp({ characterId, ...input }).then((next) => {
                      // Повышение прошло — экран закрывается сам: держать
                      // его открытым значит предлагать поднять уровень
                      // второй раз подряд. Что именно случилось, говорит
                      // сообщение на листе: иначе повышение неотличимо
                      // от «ничего не произошло».
                      // Сборка до заданного уровня (D60): пока не дошли,
                      // экран повышения остаётся открытым — иначе игрок
                      // возвращался бы на лист и жал «поднять уровень»
                      // столько раз, сколько уровней ему назначил мастер.
                      const more = buildTo !== null && next.level < buildTo
                      setLevelling(more)
                      setNotice(
                        more
                          ? t('level.building', {
                              level: next.level,
                              count: buildTo - next.level,
                            })
                          : t('level.done', { level: next.level }),
                      )
                      return next
                    }),
                    (error) => {
                      if (error instanceof CreationRefused) {
                        const grouped: Record<string, string[]> = {}
                        for (const problem of error.problems) {
                          grouped[problem.slot] = [
                            ...(grouped[problem.slot] ?? []),
                            problem.message,
                          ]
                        }
                        setLevelProblems(grouped)
                        return true
                      }
                      return false
                    },
                  )
                }}
                onLevelDown={() => {
                  runCommand(
                    api.levelDown(characterId).then((next) => {
                      setLevelling(false)
                      setNotice(t('level.undone', { level: next.level }))
                      return next
                    }),
                    () => false,
                  )
                }}
                onClose={() => {
                  setLevelling(false)
                }}
              />
            ) : (
              <>
                <CharacterSheetTab
                  sheet={sheet}
                  busy={busy}
                  onRoll={(reason) => {
                    roll(reason, {})
                  }}
                  onResource={resourceAction}
                  problems={resourceProblems}
                  onDetails={(patch) => {
                    runCommand(
                      api.updateCharacter({ characterId, ...patch }),
                      () => false,
                    )
                  }}
                />
                <div className="app-screen app-actions">
                  <button
                    type="button"
                    className="app-button"
                    onClick={() => {
                      setLevelling(true)
                    }}
                  >
                    {t('level.open')}
                  </button>
                </div>
              </>
            ))}
        </div>
      </div>
      {campaignId !== null && (
        <div className="app-screen">
          <InitiativePanel
            api={api}
            campaignId={campaignId}
            initiative={table.initiative}
            master={false}
            onChanged={reloadTable}
          />
          <RollFeed rolls={table.rolls} />
        </div>
      )}

      {/* Файл персонажа (D54) и печатный лист (D64): выгрузка с листа,
          где на него и смотрят. Драфт живёт на устройстве, и файл —
          единственное, что переживёт чистку браузера. */}
      <ExportMenu api={api} sheet={sheet} onPrint={onPrint} />

      <div className="app-screen app-actions">
        <button type="button" className="app-button app-button-quiet" onClick={onBack}>
          {t('characters.back')}
        </button>
      </div>

      {/* Роллер — с любой вкладки листа: кости бросают и в бою,
          и посреди разговора, и вкладка для этого ни при чём (D12). */}
      {rolling ? (
        <RollPanel
          sheet={sheet}
          rolls={rolls}
          busy={busy}
          failure={commandFailure}
          last={lastRoll}
          mode={mode}
          onMode={setMode}
          canHide={signedIn && campaignId !== null}
          hidden={hiddenRoll}
          onHidden={setHiddenRoll}
          onRoll={roll}
          onClose={() => {
            setRolling(false)
            // Панель закрывают в том числе после отказа: оставить
            // его на потом — значит встретить им же при следующем
            // открытии, когда чинить уже нечего.
            setCommandFailure(null)
          }}
        />
      ) : (
        <button
          type="button"
          className="app-roll-open"
          data-testid="open-roll-panel"
          onClick={() => {
            setRolling(true)
          }}
        >
          {t('roll.open')}
        </button>
      )}
    </>
  )
}
